import React, { useState } from 'react';
import UploadPanel from './components/UploadPanel';

function NotebookSidebar({
  notebooks,
  currentNotebook,
  onSelectNotebook,
  onCreateNotebook,
  onDeleteNotebook,
  onRenameNotebook,
  onCloseSidebar
}) {
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newNotebookTitle, setNewNotebookTitle] = useState('');
  const [viewMode, setViewMode] = useState('notebooks'); // 'notebooks' or 'uploads'
  const [editingNotebookId, setEditingNotebookId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const handleCreate = () => {
    if (newNotebookTitle.trim()) {
      onCreateNotebook(newNotebookTitle);
      setNewNotebookTitle('');
      setShowNewDialog(false);
    } else {
      onCreateNotebook();
      setShowNewDialog(false);
    }
  };

  const handleDelete = (e, notebookId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this notebook?')) {
      onDeleteNotebook(notebookId);
    }
  };

  const startEditing = (e, notebook) => {
    e.stopPropagation();
    setEditingNotebookId(notebook.id);
    setEditTitle(notebook.title);
  };

  const handleRename = (e) => {
    e.stopPropagation();
    if (editTitle.trim()) {
      onRenameNotebook(editingNotebookId, editTitle);
      setEditingNotebookId(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRename(e);
    } else if (e.key === 'Escape') {
      setEditingNotebookId(null);
    }
  };

  return (
    <div className="w-64 bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <img src="/logo/sciFi_logo_2_transprent.png" alt="sciFi" className="h-8 w-auto" />
          <button
            onClick={onCloseSidebar}
            className="text-gray-400 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <button
          onClick={() => setShowNewDialog(true)}
          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Notebook
        </button>
      </div>



      {/* View Toggle */}
      <div className="flex border-b border-gray-700">
        <button
          onClick={() => setViewMode('notebooks')}
          className={`flex-1 py-2 text-sm font-medium ${viewMode === 'notebooks'
            ? 'text-white border-b-2 border-blue-500'
            : 'text-gray-400 hover:text-gray-300'
            }`}
        >
          Notebooks
        </button>
        <button
          onClick={() => setViewMode('uploads')}
          className={`flex-1 py-2 text-sm font-medium ${viewMode === 'uploads'
            ? 'text-white border-b-2 border-blue-500'
            : 'text-gray-400 hover:text-gray-300'
            }`}
        >
          Workspace
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {viewMode === 'notebooks' ? (
          <div className="flex-1 overflow-y-auto p-2">
            <div className="text-xs font-semibold text-gray-400 px-2 mb-2 uppercase">
              Notebooks
            </div>
            <div className="space-y-1">
              {notebooks.length === 0 ? (
                <div className="text-sm text-gray-400 text-center py-8">
                  No notebooks yet
                </div>
              ) : (
                notebooks.map((notebook) => (
                  <div
                    key={notebook.id}
                    onClick={() => onSelectNotebook(notebook.id)}
                    className={`group relative px-3 py-2 rounded-lg cursor-pointer transition-colors ${currentNotebook === notebook.id
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-gray-800 text-gray-300'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        {editingNotebookId === notebook.id ? (
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => setEditingNotebookId(null)}
                            autoFocus
                            className="w-full px-2 py-1 text-sm text-gray-900 bg-white rounded border border-blue-500 focus:outline-none"
                          />
                        ) : (
                          <>
                            <div className="text-sm font-medium truncate">
                              {notebook.title}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {new Date(notebook.updated_at).toLocaleDateString()}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => startEditing(e, notebook)}
                          className="p-1 hover:bg-green-700 rounded text-green-400 hover:text-white"
                          title="Rename"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, notebook.id)}
                          className="p-1 hover:bg-red-600 rounded text-gray-400 hover:text-white"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden bg-gray-100 text-gray-900">
            <UploadPanel />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-700">
        <div className="text-xs text-gray-400">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Connected to API</span>
          </div>
          <div>
            {notebooks.length} notebook{notebooks.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* New Notebook Dialog */}
      {
        showNewDialog && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-80 text-gray-900">
              <h3 className="text-lg font-bold mb-4">Create New Notebook</h3>
              <input
                type="text"
                value={newNotebookTitle}
                onChange={(e) => setNewNotebookTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="Notebook title (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowNewDialog(false);
                    setNewNotebookTitle('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}

export default NotebookSidebar;
